import { MAX_HOUSEHOLD_RECIPES } from '@platewell/core';
import type { ImportFileDto } from '@platewell/schema';
import { useCollections, useConnectionStatus, useImportCollectionId, usePlatewell, useStore } from '@platewell/query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getImportSession, patchImportFile, requestImportFileExtraction } from '../lib/api.js';
import { createExtractPacer, extractionWeight, needsExtraction } from '../lib/extract-pacer.js';
import { importCompletionMessage, importOneFile, isClientProcessable } from '../lib/import-runner.js';
import { readLocalImportContents } from '../lib/import-local.js';
import { quotaRefusal, UpgradeNotice, type QuotaRefusal } from '../components/upgrade-notice.js';
import { importSessionRoute } from '../router.js';

const POLL_INTERVAL_MS = 1500;

export function ImportSessionPage() {
  const { importId } = importSessionRoute.useParams();
  // Set when the run was started from a collection, so its recipes are filed there rather
  // than in the household's import collection.
  const { collectionId: targetCollectionId } = importSessionRoute.useSearch();
  const { activeHouseholdId } = usePlatewell();
  const store = useStore();
  const { online } = useConnectionStatus();
  const navigate = useNavigate();
  const [files, setFiles] = useState<ImportFileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);
  const [forcingId, setForcingId] = useState<string | null>(null);
  const runningRef = useRef(false);
  // One limiter per session, holding the per-minute OpenAI budget this tab may spend.
  const pacerRef = useRef<ReturnType<typeof createExtractPacer> | null>(null);
  // Files already handed to the limiter. The 1.5s poll re-renders constantly, so
  // without this every tick would schedule the same pending files all over again.
  const requestedRef = useRef(new Set<string>());
  const forceIdsRef = useRef(new Set<string>());
  const localContentRef = useRef(readLocalImportContents(importId));
  // When a single-file import is still working when the page opens, we jump straight to the
  // recipe once it succeeds. Sessions that are already complete on load (e.g. reopened from
  // history) skip the redirect so the user stays on the summary they navigated to.
  const autoNavigateRef = useRef(false);
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (activeHouseholdId == null) return;
    setLoading(true);
    void getImportSession(activeHouseholdId, importId)
      .then(async (session) => {
        const onlyFile = session.files.length === 1 ? session.files[0] : null;
        autoNavigateRef.current = onlyFile != null && onlyFile.status !== 'success';
        const reset = session.files.filter((file) => file.status === 'in-progress');
        if (reset.length > 0) {
          const updated = await Promise.all(
            reset.map((file) =>
              patchImportFile(activeHouseholdId, importId, file.id, {
                status: file.sourceKind === 'json' ? 'pending' : 'ready',
              }),
            ),
          );
          const byId = new Map(updated.map((file) => [file.id, file]));
          setFiles(session.files.map((file) => byId.get(file.id) ?? file));
        } else {
          setFiles(session.files);
        }
      })
      .catch((cause) => {
        setError(cause instanceof Error ? cause.message : 'Could not load import session');
      })
      .finally(() => setLoading(false));
  }, [activeHouseholdId, importId]);

  // Poll while any AI extraction is still running.
  useEffect(() => {
    if (!online || activeHouseholdId == null || loading) return;
    const waiting = files.some(
      (file) => file.status === 'pending' && file.sourceKind !== 'json'
        || file.status === 'extracting',
    );
    if (!waiting) return;

    const timer = window.setInterval(() => {
      void getImportSession(activeHouseholdId, importId)
        .then((session) => setFiles(session.files))
        .catch(() => {
          /* keep last known state */
        });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [activeHouseholdId, files, importId, loading, online]);

  // Ask for extractions at a pace that stays inside the OpenAI limits. Sessions small
  // enough to be safe were already enqueued by the server and arrive past `pending`,
  // so this mostly does work for the large batches that need pacing — plus any file
  // left stranded at `extracting` by a server that died mid-job.
  useEffect(() => {
    if (!online || activeHouseholdId == null || loading) return;
    const now = Date.now();
    const waiting = files.filter(
      (file) => needsExtraction(file, now) && !requestedRef.current.has(file.id),
    );
    if (waiting.length === 0) return;

    pacerRef.current ??= createExtractPacer();
    const pacer = pacerRef.current;

    for (const file of waiting) {
      // Claim before scheduling, not inside the job: the limiter may hold a job for
      // minutes, and several polls will have run by the time it starts.
      requestedRef.current.add(file.id);
      void pacer
        .schedule({ weight: extractionWeight(file) }, () =>
          requestImportFileExtraction(activeHouseholdId, importId, file.id),
        )
        .then((updated) => {
          setFiles((current) =>
            current.map((entry) => (entry.id === updated.id ? updated : entry)),
          );
        })
        .catch(() => {
          // Let it be retried on a later poll rather than stranding the file.
          requestedRef.current.delete(file.id);
        });
    }
  }, [activeHouseholdId, files, importId, loading, online]);

  // Navigating away must not leave the limiter firing requests for a page nobody is on.
  useEffect(() => {
    return () => {
      void pacerRef.current?.stop({ dropWaitingJobs: true });
      pacerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!online || activeHouseholdId == null || loading || runningRef.current) return;
    const next = files.find((file) => isClientProcessable(file));
    if (next == null) return;

    // Stop the whole run at the plan limit rather than letting 300 files each fail
    // individually with the same message. `importOneFile` would catch the throw and mark
    // the file failed, which is correct but is not worth doing 300 times.
    if (!store.canAddRecipe()) {
      setRefusal({
        limit: store.getRecipeLimit(),
        remaining: 0,
        ceiling: store.liveRecipeCount() >= MAX_HOUSEHOLD_RECIPES,
      });
      return;
    }
    // Room again — a recipe was trashed, or the household subscribed on another device —
    // so the run picks up where it stopped and the prompt goes with it.
    setRefusal(null);

    const force = forceIdsRef.current.has(next.id);
    if (force) forceIdsRef.current.delete(next.id);

    runningRef.current = true;
    void importOneFile({
      store,
      householdId: activeHouseholdId,
      importId,
      file: next,
      localContent: localContentRef.current[next.id],
      force,
      collectionId: targetCollectionId,
    })
      .then((updated) => {
        setFiles((current) => current.map((file) => (file.id === updated.id ? updated : file)));
      })
      .catch((cause) => {
        // A household that filled up mid-run lands here rather than at the check above,
        // because the slot went between the two.
        const quota = quotaRefusal(cause);
        if (quota != null) setRefusal(quota);
        else setError(cause instanceof Error ? cause.message : 'Import failed');
      })
      .finally(() => {
        runningRef.current = false;
        setForcingId(null);
      });
  }, [activeHouseholdId, files, importId, loading, online, store, targetCollectionId]);

  // Once a lone imported recipe succeeds, take the user straight to it.
  useEffect(() => {
    if (!autoNavigateRef.current || navigatedRef.current) return;
    if (files.length !== 1) return;
    const only = files[0];
    if (only == null || only.status !== 'success' || only.recipeId == null) return;
    navigatedRef.current = true;
    void navigate({ to: '/recipes/$recipeId', params: { recipeId: only.recipeId } });
  }, [files, navigate]);

  const forceImport = useCallback(
    async (file: ImportFileDto) => {
      if (activeHouseholdId == null || forcingId != null) return;
      setForcingId(file.id);
      setError(null);
      forceIdsRef.current.add(file.id);
      try {
        const updated = await patchImportFile(activeHouseholdId, importId, file.id, {
          status: file.sourceKind === 'json' ? 'pending' : 'ready',
          error: '',
        });
        setFiles((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      } catch (cause) {
        forceIdsRef.current.delete(file.id);
        setForcingId(null);
        setError(cause instanceof Error ? cause.message : 'Could not force import');
      }
    },
    [activeHouseholdId, forcingId, importId],
  );

  const collections = useCollections();
  const importCollectionId = useImportCollectionId();
  const targetCollectionName = useMemo(() => {
    const id = targetCollectionId ?? importCollectionId;
    return collections.find((collection) => collection.id === id)?.name ?? 'Main';
  }, [collections, importCollectionId, targetCollectionId]);
  const completion = useMemo(
    () => importCompletionMessage(files, targetCollectionName),
    [files, targetCollectionName],
  );

  const progressPercent = useMemo(() => {
    if (files.length === 0) return 0;
    const done = files.filter(
      (file) =>
        file.status === 'success' || file.status === 'fail' || file.status === 'duplicate',
    ).length;
    return Math.round((done / files.length) * 100);
  }, [files]);

  if (loading) {
    return <p className="hint">Loading import session…</p>;
  }

  return (
    <div className="editor" style={{ maxWidth: 960 }}>
      <div className="row" style={{ marginBottom: 12 }}>
        <h1>Import Session</h1>
        <span className="spacer" />
        <Link className="btn ghost" to="/imports">
          All imports
        </Link>
      </div>

      {files.length > 0 && (
        <div
          className="import-progress"
          role="progressbar"
          aria-label="Import progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPercent}
        >
          <div className="import-progress-track">
            <div className="import-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <span className="import-progress-label">{progressPercent}%</span>
        </div>
      )}

      {!online && (
        <p className="error-text">Import is paused while offline. Reconnect to continue.</p>
      )}
      {refusal != null && <UpgradeNotice refusal={refusal} action="import the rest" />}
      {error != null && <p className="error-text">{error}</p>}
      {completion != null && <p className="import-complete">{completion}</p>}

      <table className="import-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Result</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => {
            const linkedRecipeId =
              (file.status === 'success' || file.status === 'duplicate') && file.recipeId != null
                ? file.recipeId
                : null;
            const recipeTitle =
              linkedRecipeId != null ? (store.getRecipe(linkedRecipeId)?.title ?? null) : null;

            return (
              <tr key={file.id} className={`import-table-row import-table-row--${file.status}`}>
                <td className="import-table-file">{file.originalFilename}</td>
                <td className="import-table-result">
                  <ImportFileResult
                    file={file}
                    linkedRecipeId={linkedRecipeId}
                    recipeTitle={recipeTitle}
                    online={online}
                    forcingId={forcingId}
                    onForceImport={forceImport}
                  />
                </td>
                <td className="import-table-status">
                  <span className={`import-status import-status--${file.status}`}>
                    {statusLabel(file.status)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ImportFileResultProps {
  file: ImportFileDto;
  linkedRecipeId: string | null;
  recipeTitle: string | null;
  online: boolean;
  forcingId: string | null;
  onForceImport: (file: ImportFileDto) => void;
}

function ImportFileResult({
  file,
  linkedRecipeId,
  recipeTitle,
  online,
  forcingId,
  onForceImport,
}: ImportFileResultProps) {
  if (file.status === 'fail') {
    return <span className="error-text">{file.error ?? 'Import failed'}</span>;
  }

  if (file.status === 'pending') {
    return (
      <span className="hint">
        {file.sourceKind === 'json' ? 'Waiting to import…' : 'Queued for AI extraction…'}
      </span>
    );
  }

  if (file.status === 'extracting') {
    return <span className="hint">Extracting recipe with AI…</span>;
  }

  if (file.status === 'ready') {
    return <span className="hint">Ready to import…</span>;
  }

  if (file.status === 'in-progress') {
    return <span className="hint">Importing…</span>;
  }

  if (linkedRecipeId != null) {
    return (
      <div className="import-table-result-linked">
        <Link
          className="import-file-recipe-link"
          to="/recipes/$recipeId"
          params={{ recipeId: linkedRecipeId }}
        >
          {recipeTitle ??
            (file.status === 'duplicate' ? 'View existing recipe' : 'View recipe')}
        </Link>
        {file.status === 'duplicate' && (
          <>
            {file.error != null && <span className="hint import-table-duplicate-note">{file.error}</span>}
            <button
              type="button"
              className="btn ghost import-file-force"
              disabled={!online || forcingId === file.id}
              onClick={() => void onForceImport(file)}
            >
              {forcingId === file.id ? 'Importing…' : 'Force import'}
            </button>
          </>
        )}
      </div>
    );
  }

  return <span className="hint">—</span>;
}

function statusLabel(status: ImportFileDto['status']): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'extracting':
      return 'Extracting…';
    case 'ready':
      return 'Ready';
    case 'in-progress':
      return 'Importing…';
    case 'success':
      return 'Success';
    case 'fail':
      return 'Failed';
    case 'duplicate':
      return 'Duplicate';
  }
}
